import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  reportOffline,
  reportOnline,
  reportSlowNetwork,
  reportFastNetwork,
} from '../store/loading.actions';

type NetworkInformation = {
  effectiveType?: string;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
};

declare global {
  interface Navigator {
    connection?: NetworkInformation;
  }
}

// Network Information API classifies effectiveType using observed RTT and downlink:
//   slow-2g: RTT >= 2000ms or downlink <= 50 Kbps
//   2g:      RTT >= 1400ms or downlink <= 70 Kbps
//   3g:      RTT >= 270ms  or downlink <= 700 Kbps
//   4g:      everything else
// Note: effectiveType is RTT-dominant — low bandwidth alone (e.g. 1 Kbps with
// normal latency) may still report "4g". Request-timing detection in the Axios
// interceptor (station-tools-api.ts) covers that gap.
const SLOW_EFFECTIVE_TYPES = new Set(['slow-2g', '2g']);

const useNetworkStatus = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!navigator.onLine) {
      dispatch(reportOffline());
    }

    const handleOffline = () => dispatch(reportOffline());
    const handleOnline = () => dispatch(reportOnline());

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    const connection = navigator.connection;
    const handleConnectionChange = () => {
      if (
        connection?.effectiveType &&
        SLOW_EFFECTIVE_TYPES.has(connection.effectiveType)
      ) {
        dispatch(reportSlowNetwork());
      } else {
        dispatch(reportFastNetwork());
      }
    };

    if (connection) {
      if (
        connection.effectiveType &&
        SLOW_EFFECTIVE_TYPES.has(connection.effectiveType)
      ) {
        dispatch(reportSlowNetwork());
      }
      connection.addEventListener('change', handleConnectionChange);
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      connection?.removeEventListener('change', handleConnectionChange);
    };
  }, [dispatch]);
};

export default useNetworkStatus;
